import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { trackFieldFocus } from '@/domain/job-application/helpers';
import { LIFormFieldValues, LOFormFieldValues } from '@/domain/job-application/types';
import { useBreakpoints } from '@/hooks';

import { FormField } from './form-fields.common.styles';

const DEFAULT_ADDITIONAL_COMMENTS_CHARACTER_LIMIT = 500;
const ONE_EXTRA_CHAR_LEFT = -1;

const StyledFormField = styled(FormField)`
  p:last-of-type {
    text-align: right;
  }
`;

const onCommentsFocus = (): void => {
  trackFieldFocus({
    isAutofilled: false,
    isMandatory: false,
    fieldName: 'comments',
  });
};

type Props = {
  control: Control<LOFormFieldValues | LIFormFieldValues>;
};

export const CommentsField: React.VFC<Props> = ({ control }) => {
  const intl = useIntl();
  const { isMobile } = useBreakpoints();

  return (
    <Controller
      name="comments"
      control={control}
      render={({ field: { ref, ...fieldProps } }) => {
        const charsLeft = DEFAULT_ADDITIONAL_COMMENTS_CHARACTER_LIMIT - fieldProps.value.length;
        return (
          <StyledFormField
            multiline
            size="large"
            minRows={isMobile ? 7 : 4}
            {...fieldProps}
            aria-label={intl.formatMessage({ id: 'JOBS_POSTING_EASY_APPLY_FORM_ADDITIONAL_COMMENTS_HEADLINE' })}
            data-cy="additional-comments"
            innerRef={ref}
            error={charsLeft < 0}
            helperText={
              charsLeft < 0
                ? intl.formatMessage(
                    {
                      id:
                        charsLeft === ONE_EXTRA_CHAR_LEFT
                          ? 'JOBS_POSTING_APPLY_FORM_LO_COMMENTS_FIELD_ERROR_ONE'
                          : 'JOBS_POSTING_APPLY_FORM_LO_COMMENTS_FIELD_ERROR',
                    },
                    {
                      number: Math.abs(charsLeft),
                    }
                  )
                : `${intl.formatMessage(
                    {
                      id: 'JOBS_POSTING_APPLY_FORM_LO_COMMENTS_FIELD_HELPER',
                    },
                    {
                      number: charsLeft,
                    }
                  )}`
            }
            onFocus={onCommentsFocus}
          />
        );
      }}
      rules={{
        maxLength: DEFAULT_ADDITIONAL_COMMENTS_CHARACTER_LIMIT,
      }}
    />
  );
};
